import React, { useEffect, useState } from 'react';
import Auth from 'works/legacy/lib/auth';
import { Button, Checkbox } from 'antd';
import { Link, Modal } from '@common/components';
import { Colors } from 'works/legacy/lib/style-helper';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { UpdateCurrentWorksUserDetailsMutation } from '@identity/graphql';

type Props = {
  open: boolean;
  userHasAlreadyDecided: boolean;
  onClose: () => void;
  onRedirectToSavedRoute: () => void;
};

export function LastVisitedPageModal({
  open,
  userHasAlreadyDecided,
  onClose,
  onRedirectToSavedRoute,
}: Props) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open && userHasAlreadyDecided) {
      onRedirectToSavedRoute();
    }
  }, [open, onRedirectToSavedRoute, userHasAlreadyDecided]);

  function onError() {
    createMessage('error', 'Something went wrong, try again later.');
    onClose();
  }

  const [saveMutation, { loading }] = useMutation(
    UpdateCurrentWorksUserDetailsMutation,
    {
      onCompleted(data) {
        if (data?.updateCurrentWorksUserDetails.currentWorksUser) {
          createMessage('success', 'Preference saved successfully');

          const { saveLastVisitedPage } =
            data.updateCurrentWorksUserDetails.currentWorksUser;

          Auth.update({ saveLastVisitedPage });
          return;
        }

        onError();
      },
      onError,
    }
  );

  function savePreference(value: boolean) {
    saveMutation({
      variables: {
        input: {
          saveLastVisitedPage: value,
        },
      },
    });
  }

  const onPrimaryButtonClick = () => {
    if (checked) {
      savePreference(true);
    }

    onClose();
    onRedirectToSavedRoute();
  };

  const onSecondaryButtoonClick = () => {
    savePreference(false);
    onClose();
    onRedirectToSavedRoute();
  };

  return (
    <Modal
      visible={open && !userHasAlreadyDecided}
      okText={checked ? 'Confirm' : 'Skip'}
      cancelText="Don't ask me again"
      title="Make the last visited page your default landing page?"
      confirmLoading={loading}
      onCancel={onClose}
      footer={
        <>
          <Button onClick={onSecondaryButtoonClick}>
            Don&apos;t ask me again
          </Button>
          <Button type="primary" onClick={onPrimaryButtonClick}>
            {checked ? 'Confirm' : 'Skip'}
          </Button>
        </>
      }
    >
      <Checkbox
        checked={checked}
        onChange={(e) => setChecked(e.target.checked)}
      >
        Default landing page is the last visited page before logging out
      </Checkbox>
      <div
        style={{
          color: Colors.midnightLighter,
          fontSize: 14,
          lineHeight: '22px',
          marginTop: 8,
        }}
      >
        You can always change that in{' '}
        <Link to="/works/account-settings/user-settings">Account Settings</Link>
        .
      </div>
    </Modal>
  );
}
